import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { Me, Message } from "@/api/types";

/** The read-only demo session (FW-34): static content served by
 *  ``/api/demo-session``, never a session row. Its id is not a UUID, so
 *  the session routes refuse it. */
export const DEMO_SESSION_ID = "demo";
export const DEMO_SESSION_PATH = "/app/sessions/demo";

export type DemoDocument = {
  key: "architecture" | "roadmap";
  title: string;
  artifact_format: "docx" | "pptx";
  available_formats: Array<"pdf" | "docx" | "pptx">;
  /** The language the files (and this title) are in: the reader's when
   *  it is rendered (en, sv, fi), else the closest one. */
  language: string;
  /** The assistant message that delivered it. */
  message_id: string;
  created_at: string;
};

export type DemoDiffFile = {
  path: string;
  additions: number;
  deletions: number;
  new: boolean;
};

export type DemoMergeRequest = {
  iid: number;
  title: string;
  description: string;
  source_branch: string;
  target_branch: string;
  message_id: string;
  created_at: string;
  files: DemoDiffFile[];
  diff: string;
};

export type DemoSession = {
  id: typeof DEMO_SESSION_ID;
  /** The language the content is in (the UI language, else en). */
  language: string;
  title: string;
  repository: string;
  branch: string;
  messages: Message[];
  documents: DemoDocument[];
  merge_request: DemoMergeRequest;
};

/** Whether the demo belongs in this user's session list. */
export function demoSessionVisible(me: Me | undefined): boolean {
  return me?.features?.demo_session_enabled === true && me.user.demo_session_hidden !== true;
}

export function useDemoSession(lang: string, enabled = true) {
  return useQuery({
    queryKey: ["demo-session", lang] as const,
    queryFn: async () =>
      (await api.get<DemoSession>("/demo-session", { params: { lang } })).data,
    enabled,
    // Static content: it changes only with a deploy.
    staleTime: 60 * 60_000,
  });
}

function withLang(path: string, lang: string, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams({ ...extra, lang });
  return `/api/demo-session${path}?${params.toString()}`;
}

export function demoDocumentInlineHref(key: DemoDocument["key"], lang: string): string {
  return withLang(`/documents/${key}/inline`, lang);
}

export function demoDocumentDownloadHref(
  key: DemoDocument["key"],
  format: DemoDocument["available_formats"][number],
  lang: string,
): string {
  return withLang(`/documents/${key}/download`, lang, { format });
}

/** Hide the demo from the session list, or show it again
 *  (``PATCH /auth/me/preferences`` ``demo_session_hidden``). */
export function useSetDemoSessionHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["me", "demo-session-hidden"] as const,
    mutationFn: async (hidden: boolean) =>
      (
        await api.patch<{ demo_session_hidden: boolean }>("/auth/me/preferences", {
          demo_session_hidden: hidden,
        })
      ).data,
    onSuccess: (data) => {
      // Flip it in the cached /me at once, so the list entry goes (or
      // comes back) without waiting for the refetch.
      qc.setQueryData<Me>(keys.me, (prev) =>
        prev ? { ...prev, user: { ...prev.user, demo_session_hidden: data.demo_session_hidden } } : prev,
      );
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}
